'use client';
/* eslint-disable jsx-a11y/no-autofocus -- custom Modal prop focuses through react-focus-lock. */
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Card } from '@osmosis/tailwind-ui/components/Card';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import {
  bgPrimary,
  bg0,
  bgPrimaryLightest,
} from '@osmosis/tailwind-ui/styles/background';
import React, { useState } from 'react';

import InputValidation from '../../../components/InputValidation';
import { SubmitHandler, useForm } from 'react-hook-form';
import InputLabel from '../../../components/auth/InputLabel';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import md5 from 'md5';
import { useFetch } from '../../../context/FetchProvider';
import { FormattedMessage, useIntl } from 'react-intl';
import InputValidationMessage from '../../../components/InputValidationMessage';
import { authMessages } from '../../../constants/authLocalization';
import ElsevierSignInExperienceHeader from '../../../components/learn/ElsevierSignInExperienceHeader';
import useModal from '../../../hooks/useModal';
import Modal from '../../../components/Modal';
import ForgotPassword from '../../../components/auth/ForgotPassword';
import LoginFormFooter from '../../../components/auth/LoginFormFooter';
import { Route } from '../../../hooks/useRouterPush';
import useRouterPush from '../../../hooks/useRouterPush';
import { usePagesContext } from '../../../context/PagesProvider';

type Props = {
  email?: string;
  showPassNotice?: boolean;
};

type LoginPasswd = {
  usedTemp: boolean;
  appCode: string;
  loggedIn: boolean;
  ssoURL?: string;
};

type FormValues = { email: string; password: string };

/**
 * AuthForm.
 */
const AuthForm = (props: Props) => {
  const { email, showPassNotice } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showInvalidLoginMsg, setShowInvalidLoginMsg] = useState(false);

  const { hide, isVisible, setIsVisible } = useModal();

  const intl = useIntl();
  const routerPush = useRouterPush();

  const invalidLoginMsg = intl.formatMessage(authMessages.invalidLoginMsg);
  const requiredMsg = intl.formatMessage(authMessages.requiredMsg);
  const emailMsg = intl.formatMessage(authMessages.emailMsg);

  const validationSchema = yup
    .object({
      email: yup
        .string()
        .email(emailMsg)
        .required(
          intl.formatMessage({
            defaultMessage: 'Email is required',
            id: 'auth-form.emailRequired',
          }),
        ),
      password: yup.string().required(
        intl.formatMessage({
          defaultMessage: 'Password is required',
          id: 'auth-form.passwordRequired',
        }),
      ),
    })
    .required(requiredMsg);

  const { push } = usePagesContext();
  const { post } = useFetch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setFocus,
    setValue,
  } = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit: SubmitHandler<FormValues> = ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setIsLoading(true);
    setShowInvalidLoginMsg(false);
    const persist = 1;

    post<LoginPasswd, { email: string; password: string; persist: number }>(
      '/loginPasswd',
      {
        email,
        password: md5(password.trim()),
        persist,
      },
    )
      .then((res) => {
        if (res.data.ssoURL) {
          window.location.href = res.data.ssoURL;
          return;
        }

        if (res.data.usedTemp) {
          push('/account/profile?item=passwd');

          return;
        }

        void push('/home/dashboard');
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response?.data.error.msg === 'Invalid login.') {
          setShowInvalidLoginMsg(true);
        }
      });
  };

  const emailErrorMsg = errors?.email?.message?.toString();
  const passwordErrorMsg = errors?.password?.message?.toString();

  const hideModal = () => {
    const email = getValues('email');
    if (!email) {
      setTimeout(() => {
        setFocus('email');
      }, 50);
    } else {
      setTimeout(() => {
        setFocus('password');
      }, 50);
    }
    hide();
  };

  const forgotPasswordOnClose = (mail: string | undefined) => {
    hide();
    if (mail) {
      setTimeout(() => {
        setValue('email', mail);
        setFocus('password');
      }, 50);
    } else {
      setTimeout(() => {
        setFocus('email');
      }, 50);
    }
  };

  const signUpAction = () => {
    routerPush(Route.CREATE);
  };

  return (
    <Card className="w-full max-w-[580px] p-0">
      <ElsevierSignInExperienceHeader
        containerStyle={{ backgroundColor: `${bgPrimaryLightest}` }}
      />

      <Card style={{ border: 0 }} className="p-8 ">
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputLabel
            content={
              <FormattedMessage id="auth-form.email" defaultMessage="Email" />
            }
            htmlFor="email"
            hasError={!!emailErrorMsg}
          />

          <InputValidation
            showErrorMessage={false}
            error={!!emailErrorMsg}
            variant="filled"
            inputProps={{
              ...register('email', {
                onChange: () => setShowInvalidLoginMsg(false),
                value: email ? email : '',
              }),
              placeholder: 'Enter email (School email preferred)',
              ['data-testid']: 'auth-form.email-input',
              autoComplete: 'section-auth-form email',
            }}
            containerProps={{
              cn: 'mb-3',
            }}
          />

          <InputValidationMessage
            messageProps={{
              role: 'alert',
              style: {
                marginBottom: '18px',
              },
            }}
            error={showPassNotice}
            errorMessage={
              'To complete the Elsevier sign-in experience check your inbox for a one time temporary password and enter in the box below.'
            }
          />

          <InputLabel
            content={
              <FormattedMessage
                id="auth-form.password"
                defaultMessage="Password"
              />
            }
            htmlFor="password"
            hasError={!!passwordErrorMsg}
          />

          <InputValidation
            showErrorMessage={false}
            error={!!passwordErrorMsg}
            variant="filled"
            inputProps={{
              ...register('password', {
                onChange: () => setShowInvalidLoginMsg(false),
              }),
              placeholder: intl.formatMessage({
                id: 'auth-form.password-placeholder',
                defaultMessage: 'Enter password',
              }),
              type: 'password',
              ['data-testid']: 'auth-form.password-input',
              autoComplete: 'section-auth-form current-password',
            }}
            containerProps={{
              cn: 'mb-3',
            }}
          />

          <Button
            size="l"
            variant="outline"
            type="submit"
            disabled={isLoading}
            isLoading={isLoading}
            loadingSpinnerProps={{ size: 20, color: 'textInvert' }}
            data-testid="auth-form.next-btn"
            className={`w-full ${bg0} ${bgPrimary} rounded-[100px]`}
          >
            <FormattedMessage id="common.next" defaultMessage="Next" />
          </Button>

          <InputValidationMessage
            error={showInvalidLoginMsg}
            errorMessage={invalidLoginMsg}
            messageProps={{ 'data-testid': 'auth-form.invalid-login-msg' }}
          />

          <InputValidationMessage
            error={!showInvalidLoginMsg && !!emailErrorMsg}
            errorMessage={emailErrorMsg}
          />

          <InputValidationMessage
            error={!showInvalidLoginMsg && !!passwordErrorMsg}
            errorMessage={passwordErrorMsg}
          />

          <LoginFormFooter isSignUp={false} />

          <Flex className="mt-4 justify-center">
            <Button
              type="button"
              variant="transparent"
              data-testid="forgot-password-btn"
              onClick={() => setIsVisible(true)}
              className={`${bgPrimary} underline`}
            >
              <FormattedMessage
                id="login.forgot_password"
                defaultMessage="Forgot your password?"
              />
            </Button>
          </Flex>

          <Flex>
            <P>
              <FormattedMessage
                id="login.account"
                defaultMessage="Don't have an account?"
              />
            </P>
            <P
              data-testid="sign_up-button"
              style={{
                textDecoration: 'none',
              }}
              onClick={signUpAction}
              onKeyDown={(e) => e.key === 'Enter' && signUpAction()}
              tabIndex={0}
              className=""
            >
              <FormattedMessage id="login.sign_up" defaultMessage=" Sign up" />
            </P>
          </Flex>
        </form>
        <Modal hide={hideModal} isVisible={isVisible} autoFocus>
          <ForgotPassword
            redirectToLogin={false}
            onClose={forgotPasswordOnClose}
          />
        </Modal>
      </Card>
    </Card>
  );
};

export default AuthForm;
